




































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ActivityCard from './ActivityCard.vue';
import ActivityCreate from './ActivityCreate.vue';
import ActivityDetailPane from './ActivityDetailPane.vue';
import ActivitySidePanel from './ActivitySidePanel.vue';
import ActivityDto from '@/models/Activities/ActivityDto';
import ActivityType, { ActivityTypeLabels, ActivityTypeIcons } from '@/models/Activities/ActivityType';
import ActivityStatus from '@/models/Activities/ActivityStatus';
import ActivityDirection from '@/models/Activities/ActivityDirection';
import ActivityEntityType from '@/models/Activities/ActivityEntityType';
import OpportunityStage from '@/models/Opportunities/OpportunityStage';
import OpportunityStatus from '@/models/Opportunities/OpportunityStatus';
import { ActivityTimelineModule } from '@/store/modules/activityTimeline';

@Component({
    name: 'ActivityTimeline',
    components: {
        ActivityCard,
        ActivityCreate,
        ActivityDetailPane,
        ActivitySidePanel
    }
})
export default class ActivityTimeline extends GiganautVue {
    @Prop({ required: true })
    entityType!: ActivityEntityType;

    @Prop({ required: true })
    entityId!: string;

    @Prop({ required: false, default: '' })
    entityEmail!: string;

    // Optional: when the host is an Opportunity, the parent passes the full
    // OpportunityDto so the side panel can resolve recipients from linked
    // records + prospect snapshot in one go.
    @Prop({ required: false, default: null })
    entityOpportunity!: any | null;

    selectedTypes: ActivityType[] = [ActivityType.Note, ActivityType.Call, ActivityType.Meeting, ActivityType.Email];
    deleteDialogVisible = false;
    deletingActivity: ActivityDto | null = null;

    // Master-detail selection state
    selectedActivityId: string | null = null;

    // Side panel state (for create/edit only)
    sidePanelVisible = false;
    editingActivity: ActivityDto | null = null;
    createMode = false;
    createActivityType: ActivityType | null = null;
    createActivityDirection: ActivityDirection | null = null;
    createFromActivity: ActivityDto | null = null; // For creating task from email

    // Reply state
    replyToActivity: ActivityDto | null = null;
    replyAll = false;
    isChase = false;

    // Non-task activity types for filtering (tasks have their own dedicated tab)
    readonly nonTaskTypes = [ActivityType.Note, ActivityType.Call, ActivityType.Meeting, ActivityType.Email];

    get supportsIncludeRelated(): boolean {
        return this.entityType === ActivityEntityType.Company || this.entityType === ActivityEntityType.Location;
    }

    get includeRelatedLocal(): boolean {
        return ActivityTimelineModule.includeRelated;
    }

    set includeRelatedLocal(value: boolean) {
        ActivityTimelineModule.setIncludeRelated(value);
    }

    get availableTypes() {
        return [
            { value: ActivityType.Note, label: 'Notes', icon: ActivityTypeIcons[ActivityType.Note] },
            { value: ActivityType.Call, label: 'Calls', icon: ActivityTypeIcons[ActivityType.Call] },
            { value: ActivityType.Meeting, label: 'Meetings', icon: ActivityTypeIcons[ActivityType.Meeting] },
            { value: ActivityType.Email, label: 'Emails', icon: ActivityTypeIcons[ActivityType.Email] }
        ];
    }

    get loading(): boolean {
        return ActivityTimelineModule.loading;
    }

    get loadingMore(): boolean {
        return ActivityTimelineModule.loadingMore;
    }

    get loadingDialog(): boolean {
        return ActivityTimelineModule.loadingDialog;
    }

    get activities(): ActivityDto[] {
        return ActivityTimelineModule.activities;
    }

    // Activities are frozen once the host opportunity reaches a terminal state
    // — Won (stage) or Closed (status). Mirrors the backend guard in
    // activityService.opportunityAllowsActivityChanges so the UI can't offer an
    // action the API would reject.
    get activitiesLocked(): boolean {
        if (this.entityType !== ActivityEntityType.Opportunity || !this.entityOpportunity) return false;
        return this.entityOpportunity.stage === OpportunityStage.Won
            || this.entityOpportunity.status === OpportunityStatus.Closed;
    }

    get activitiesLockedReason(): string {
        if (!this.entityOpportunity) return '';
        if (this.entityOpportunity.stage === OpportunityStage.Won) {
            return 'This opportunity has been won, so its activities are locked. Reopen it to log or edit activities.';
        }
        if (this.entityOpportunity.status === OpportunityStatus.Closed) {
            return 'This opportunity is closed, so its activities are locked. Reopen it to log or edit activities.';
        }
        return '';
    }

    get hasMore(): boolean {
        return ActivityTimelineModule.hasMore;
    }

    get totalCount(): number {
        return ActivityTimelineModule.totalCount;
    }

    get selectedDetailActivity(): ActivityDto | null {
        if (!this.selectedActivityId) return null;
        return this.activities.find(a => a.id === this.selectedActivityId) || null;
    }

    async created() {
        await ActivityTimelineModule.setContext({
            entityType: this.entityType,
            entityId: this.entityId
        });
        await ActivityTimelineModule.loadCategories();
        // Exclude tasks — they have their own dedicated tab. Default to completed activities.
        await ActivityTimelineModule.setFilters({ activityTypes: [...this.nonTaskTypes], statuses: [ActivityStatus.Completed] });
    }

    beforeDestroy() {
        ActivityTimelineModule.cleanState();
    }

    toggleType(type: ActivityType) {
        const idx = this.selectedTypes.indexOf(type);
        if (idx >= 0) {
            this.selectedTypes.splice(idx, 1);
        } else {
            this.selectedTypes.push(type);
        }
    }

    @Watch('selectedTypes')
    async onTypesChange() {
        await ActivityTimelineModule.setFilters({ activityTypes: [...this.selectedTypes] });
    }

    async loadMore() {
        await ActivityTimelineModule.loadMore();
    }

    onSelect(activity: ActivityDto) {
        this.selectedActivityId = activity.id;
    }

    onCreateTypeSelected(activityType: ActivityType) {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = activityType;
        this.createActivityDirection = null;
        this.sidePanelVisible = true;
    }

    onCreateCallSelected(direction: ActivityDirection) {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Call;
        this.createActivityDirection = direction;
        this.sidePanelVisible = true;
    }

    onEmailSelected() {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Email;
        this.createActivityDirection = null;
        this.sidePanelVisible = true;
    }

    onEdit(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.editingActivity = activity;
        this.createMode = false;
        this.createActivityType = null;
        this.sidePanelVisible = true;
    }

    onResumeDraft(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.editingActivity = activity;
        this.createMode = false;
        this.createActivityType = null;
        this.sidePanelVisible = true;
    }

    onEditFromPanel(activity: ActivityDto) {
        // Side panel handles edit mode internally
    }

    async onComplete(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        await ActivityTimelineModule.completeTask(activity.id);
    }

    onDelete(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.deletingActivity = activity;
        this.deleteDialogVisible = true;
    }

    onDeleteFromPanel(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.deletingActivity = activity;
        this.deleteDialogVisible = true;
    }

    async confirmDelete() {
        if (!this.deletingActivity) return;
        const deletedId = this.deletingActivity.id;
        await ActivityTimelineModule.deleteActivity(deletedId);
        this.deleteDialogVisible = false;
        this.deletingActivity = null;
        if (this.selectedActivityId === deletedId) {
            this.selectedActivityId = null;
        }
        this.closeSidePanel();
    }

    closeSidePanel() {
        this.sidePanelVisible = false;
        this.editingActivity = null;
        this.createMode = false;
        this.createActivityType = null;
        this.createActivityDirection = null;
        this.createFromActivity = null;
        this.replyToActivity = null;
        this.replyAll = false;
        this.isChase = false;
    }

    async onActivitySaved() {
        this.closeSidePanel();
        await ActivityTimelineModule.loadTimeline();
    }

    async onActivityUpdated() {
        await ActivityTimelineModule.loadTimeline();
    }

    onReply(payload: { activity: ActivityDto; replyAll: boolean; chase?: boolean }) {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Email;
        this.createActivityDirection = null;
        this.createFromActivity = null;
        this.replyToActivity = payload.activity;
        this.replyAll = payload.replyAll;
        this.isChase = payload.chase || false;
        this.sidePanelVisible = true;
    }

    onCreateTask(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Task;
        this.createFromActivity = activity;
        this.sidePanelVisible = true;
    }
}
